import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { FileDown } from "lucide-react";

const sageColor = (s) => s === "SYNCED" ? "border-emerald-500/40 text-emerald-400"
  : s === "REQUIRES ATTENTION" ? "border-rose-500/40 text-rose-400" : "border-amber-500/40 text-amber-400";
const fmt = (iso) => new Date(iso).toLocaleString("en-IE", { timeZone: "Europe/Dublin" });

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bulk, setBulk] = useState({ user_id: "", start: "", end: "" });
  useEffect(() => {
    const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
    load();
    api.get("/admin/customers").then(({ data }) => setCustomers(data));
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const bulkHref = bulk.user_id
    ? `${API}/admin/orders/bulk-pdf?user_id=${bulk.user_id}${bulk.start ? `&start=${bulk.start}` : ""}${bulk.end ? `&end=${bulk.end}` : ""}`
    : null;

  return (
    <div>
      <h1 className="font-serif-display text-3xl font-bold text-slate-50">Orders</h1>
      <p className="mt-1 text-sm text-[#94A3B8]">M&C orders mapped to Sage Sales Orders with sync status.</p>

      <div className="mt-6 rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-5">
        <div className="flex items-center gap-2 text-slate-100"><FileDown className="h-5 w-5 text-[#D4AF37]" /> <span className="font-semibold">Bulk Order PDF Export</span></div>
        <p className="mt-1 text-xs text-[#94A3B8]">Download all of a customer's order confirmations for a date range as one combined PDF.</p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs text-[#64748B]">Customer</label>
            <select data-testid="bulk-customer-select" value={bulk.user_id} onChange={(e) => setBulk((b) => ({ ...b, user_id: e.target.value }))}
              className="rounded-xl border border-[#2A2E39] bg-[#0B0C0E] px-3 py-2.5 text-sm text-slate-100 focus:border-[#D4AF37]/60 focus:outline-none">
              <option value="">Select customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name} ({c.email})</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#64748B]">From</label>
            <input data-testid="bulk-start" type="date" value={bulk.start} onChange={(e) => setBulk((b) => ({ ...b, start: e.target.value }))}
              className="rounded-xl border border-[#2A2E39] bg-[#0B0C0E] px-3 py-2.5 text-sm text-slate-100 focus:border-[#D4AF37]/60 focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#64748B]">To</label>
            <input data-testid="bulk-end" type="date" value={bulk.end} onChange={(e) => setBulk((b) => ({ ...b, end: e.target.value }))}
              className="rounded-xl border border-[#2A2E39] bg-[#0B0C0E] px-3 py-2.5 text-sm text-slate-100 focus:border-[#D4AF37]/60 focus:outline-none" />
          </div>
          <button data-testid="bulk-download-btn" disabled={!bulkHref}
            onClick={() => { if (bulkHref) window.location.href = bulkHref; }}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm ${bulkHref ? "mc-btn-gold" : "cursor-not-allowed border border-[#2A2E39] text-[#64748B]"}`}>
            <FileDown className="h-4 w-4" /> Download PDF
          </button>
        </div>
      </div>

      <div className="mt-8 overflow-x-auto rounded-2xl border border-[#2A2E39]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#121418] text-xs uppercase tracking-wider text-[#64748B]">
            <tr>{["M&C Order", "Customer", "Items", "Total", "Status", "Sage Status", "Sage SO", "Invoice", "Created"].map((h) => <th key={h} className="whitespace-nowrap px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[#2A2E39]">
            {orders.map((o) => (
              <tr key={o.id} data-testid={`admin-order-${o.order_number}`} className="mc-table-row">
                <td className="whitespace-nowrap px-4 py-3 font-mono-data font-bold text-[#D4AF37]">{o.order_number}</td>
                <td className="px-4 py-3 text-slate-100">{o.company_name || o.user_email}</td>
                <td className="px-4 py-3 text-[#94A3B8]">{o.items.reduce((n, i) => n + i.quantity, 0)} units</td>
                <td className="px-4 py-3 font-mono-data text-slate-100">€{o.total.toFixed(2)}</td>
                <td className="px-4 py-3"><span className="rounded-full border border-[#2A2E39] px-2 py-0.5 text-xs text-slate-200">{o.status}</span></td>
                <td className="px-4 py-3"><span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${sageColor(o.sage_status)}`}>{o.sage_status}</span></td>
                <td className="px-4 py-3 font-mono-data text-slate-100">{o.sage_order_number ? `#${o.sage_order_number}` : "—"}</td>
                <td className="px-4 py-3" data-testid={`admin-invoice-${o.order_number}`}>
                  {o.sage_invoice_number
                    ? <span className="font-mono-data text-xs text-emerald-400">{o.sage_invoice_number}<span className="ml-1 text-[#64748B]">· {o.sage_invoice_status}</span></span>
                    : <span className="text-[#64748B]">—</span>}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-[#94A3B8]">{fmt(o.created_at)}</td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-[#64748B]">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
